import { pipe } from "@mobily/ts-belt"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditMove } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course, CourseCalendarDate } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { extractLesson } from "cc-frontend/lib/transferrable-date-utils"
import {
  consolidateCourseDatesOff,
  constructUnitsAndDates,
  getCardStackIdForDate,
  getCourseDateForCardStackId,
  insertRange,
} from "cc-frontend/lib/unit-utils"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"

interface Payload {
  course: Course
  dateString: string
  planbook: Planbook
  rotationCalendar: RotationCalendar
}

export function prepareBumpLesson(payload: Payload): PreparedAction {
  return {
    name: BUMP_LESSON.name,
    payload: payload,
    patches: BUMP_LESSON.patches(payload),
    undoPatches: BUMP_LESSON.undoPatches(payload),
    narrative: partial(BUMP_LESSON.narrative, payload),
  }
}

export const BUMP_LESSON = {
  name: "BUMP_LESSON",

  patches(payload: Payload): Array<PatchModel> {
    let schoolDays = payload.rotationCalendar.attributes.schoolDays
    let cardStackId = getCardStackIdForDate(
      payload.course.attributes.calendar.dates,
      payload.dateString
    )

    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      payload.course,
      payload.planbook,
      payload.rotationCalendar
    )

    let { transferrableArray, courseDatesWithoutLesson } = extractLesson(
      cardStackId,
      payload.course.attributes.calendar.dates
    )
    // Set the newStartDate to the very next day.
    // If it's not a school/course-on day, insertRange will skip ahead as needed.
    // We do this to ensure that the bump train includes any dangling lessons between payload.startDate
    // and the next course-on day.
    const newStartDate = pipe(
      payload.dateString,
      (dateString) => dateFns.parse(dateString),
      (date) => dateFns.addDays(date, 1),
      (date) => dateFns.format(date, "YYYY-MM-DD")
    )

    let newCourseDates = insertRange(
      transferrableArray,
      newStartDate,
      1,
      null,
      courseDatesWithoutLesson,
      consolidatedCourseDatesOff,
      schoolDays,
      true
    )
    let ret = constructUnitsAndDates(newCourseDates, consolidatedCourseDatesOff, schoolDays)
    let { courseDates, units } = ret

    let patch = Patch.create("course", payload.course.id)
      .set(`attributes.calendar.dates`, courseDates)
      .set(`attributes.calendar.units`, units)
      .inc(`attributes.calendar.version`, 1).patch

    // patch.unitIdsOnTheMove   = unitIdsOnTheMove
    // patch.cardStackIdsOnTheMove = cardStackIdsOnTheMove

    return [patch]
  },
  undoPatches(payload: Payload): Array<PatchModel> {
    let patch = Patch.create("course", payload.course.id)
      .set(
        "attributes.calendar.dates",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.dates))
      )
      .set(
        "attributes.calendar.units",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.units))
      )
      .inc("attributes.calendar.version", 1).patch

    return [patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
    let cardStackId = getCardStackIdForDate(
      payload.course.attributes.calendar.dates,
      payload.dateString
    )
    let courseDate: null | CourseCalendarDate = null
    let dateStringForCardStack: null | string = null

    if (payload.course && cardStackId !== null) {
      courseDate = getCourseDateForCardStackId(
        payload.course.attributes.calendar.dates,
        cardStackId
      )
    }

    if (courseDate && courseDate.type === "course-date-custom") {
      dateStringForCardStack = courseDate.attributes.dateString
    }

    let lesson = cardStackId
      ? findFn("card-stack", cardStackId) || findFn("card-stack-summary", cardStackId)
      : null

    let courseId = payload.course.id
    let planbookId = payload.planbook.id
    let lessonTitle = lesson?.attributes?.title || ""
    let newEvent: LessonEditMove = {
      ...createBlankEvent(),
      root_id: payload.planbook.id,
      root_type: "planbook",
      date_plan_scheduled_for: dateStringForCardStack,
      primary_object_id: cardStackId,
      primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
      primary_verb: "move",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),
      primary_object_type: "lesson",
      primary_object_specifier: null,

      adverbial_verb: "bumping",
      adverbial_object_type: "lesson",
      adverbial_object_specifier: "forward",
      adverbial_object_id: cardStackId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
    }
    return {
      title: "Bumped lesson",
      titleForAnalytics: "Lesson - Move",
      description: `Bumped "${lessonTitle}" forward by one day`,
      context: {
        triggeredBy: "Bump button",
        lessonTitle: lesson ? lesson.attributes.title : "[NEW LESSON]",
        courseTitle: payload.course.attributes.title,
        planbookTitle: payload.planbook.attributes.title,
        cardStackId: cardStackId ? cardStackId : "",
        courseId: payload.course.id,
        planbookId: payload.planbook.id,
      },
      activity: {
        actor: `user-${userId}`,
        verb: "edited",
        object: `lesson-${cardStackId}`,
        to: [`course-edits:course-${courseId}`, `planbook-edits:planbook-${planbookId}`],
      },
      newAnalyticsEvents: [newEvent],
    }
  },
}
